import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, IconButton, LinearProgress, Alert, Button } from '@mui/material';
import { addUserToApplyClicked, fetchCourseDetails, fetchUserData, updateUserEnrollment } from '../config/firestoreConfig';
import { useAuth } from '../context/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ReactMarkdown from 'react-markdown';


const CourseContentPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [applyUrl, setApplyUrl] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if it's mobile
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // Check if it's tablet

  useEffect(() => {
    const getCourseDetails = async () => {
      try {
        const courseDetails = await fetchCourseDetails(courseId);

        if (user && courseDetails?.enrolled_persons) {
          const enrolled = courseDetails.enrolled_persons.some(
            (enrollment) => enrollment.mailid === user.email
          );
          setIsEnrolled(enrolled);
          setApplyUrl(courseDetails.apply_url);
        }

        setCourse(courseDetails);
      } catch (error) {
        console.error("Error fetching course details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const getUserPage = async () => {
      if (user) {
        const userData = await fetchUserData(user.email);
        if (userData["enrolled"][courseId]?.current_page !== undefined) {
          setCurrentLesson(userData["enrolled"][courseId].current_page);
          setProgress(userData["enrolled"][courseId].progress);
        }
      }
    };

    getCourseDetails();
    getUserPage();
  }, [courseId, user]);

  const handleApplyJob = async () => {
    try {

      console.log(applyUrl);
      console.log(user.email);
      await addUserToApplyClicked(courseId, user.email);
      
      window.open(applyUrl, "_blank");
    } catch (error) {
      console.error("Error in handleApplyJob:", error);
    }
  };

  const handleNextLesson = async () => {
    setCurrentLesson((prevLesson) => {
      const nextLesson = Math.min(prevLesson + 1, course.content.length - 1);

      const newProgress =
        nextLesson === course.content.length - 1
          ? 100
          : ((nextLesson + 1) / course.content.length) * 100;

      setProgress(newProgress);

      const updateUserEnrollmentData = {
        id: courseId,
        progress: newProgress,
        current_page: nextLesson,
      };

      updateUserEnrollment(user.email, updateUserEnrollmentData);
      return nextLesson;
    });
  };

  const handlePreviousLesson = async () => {
    setCurrentLesson((prevLesson) => {
      const prevLessonIndex = Math.max(prevLesson - 1, 0);

      const newProgress =
        prevLessonIndex === course.content.length - 1
          ? 100
          : ((prevLessonIndex + 1) / course.content.length) * 100;

      setProgress(newProgress);

      const updateUserEnrollmentData = {
        id: courseId,
        current_page: prevLessonIndex,
      };

      updateUserEnrollment(user.email, updateUserEnrollmentData);
      return prevLessonIndex;
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isEnrolled) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Alert severity="warning" sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ fontWeight: 'bold' }}>
            You are not enrolled in this course. Please enroll to access the content.
          </Typography>
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/courses/${courseId}`)}
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #000',
            },
          }}
        >
          Go to Course Details
        </Button>
      </Container>
    );
  }

  if (!course || !course.content || course.content.length === 0) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="h6" color="error" sx={{ fontWeight: 'bold' }}>
            No content available for this course.
          </Typography>
        </Alert>

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/courses')}
          sx={{
            mt: 2,
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #000',
            },
          }}
        >
          Go to Courses Page
        </Button>
      </Container>
    );
  }

  const lesson = course.content[currentLesson];

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 6,
        mt: isMobile ? 4 : isTablet ? 6 : 10, 
      }}
    >
      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mb: 4,
          height: 10,
          borderRadius: 8,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#000',
          },
        }}
      />

      {/* Lesson Title */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 'bold',
          mb: 3,
          color: '#000',
          fontFamily: "'Roboto', sans-serif",
          fontSize: isMobile ? '1.8rem' : isTablet ? '2.2rem' : '3rem', 
        }}
      >
        Lesson {currentLesson + 1}: {lesson.title} {/* currentLesson + 1 for 1-based numbering */}
      </Typography>


      {/* Lesson Content */}
      <Box
        sx={{
          mb: 6,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: 3,
          padding: 3,
          maxWidth: '100%',
        }}
      >

        <div
          style={{
            fontSize: isMobile ? '16px' : '18px', // Responsive font size for content
            lineHeight: '1.7',
            color: '#000',
            fontFamily: "'Roboto', sans-serif",
          }}
        >
          <ReactMarkdown>{lesson.content}</ReactMarkdown>
        </div>
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2, // Space between buttons
        }}
      >
        {/* Previous Lesson Button */}
        <IconButton
          onClick={handlePreviousLesson}
          disabled={currentLesson === 0}
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #000',
            },
            borderRadius: '12px',
            padding: '12px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Next or Apply Job Button */}
        {currentLesson === course.content.length - 1 ? (
          <Button
            onClick={handleApplyJob} // Call the new function
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#fff',
                color: '#000',
                border: '1px solid #000',
              },
              borderRadius: '12px',
              padding: '12px 24px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            }}
            variant="contained"
          >
            Apply Job
        </Button>
        
        ) : (
          <IconButton
            onClick={handleNextLesson}
            disabled={currentLesson === course.content.length - 1}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#fff',
                color: '#000',
                border: '1px solid #000',
              },
              borderRadius: '12px',
              padding: '12px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        )}
      </Box>


      {/* End of Course Message */}
      {currentLesson === course.content.length - 1 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
            Congratulations! You've completed the course! 🎉
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CourseContentPage;
